import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { MessageSquareIcon, UserIcon, CalendarIcon, MapPinIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { ViewSection } from '../components/ViewPageLayout'

export function MessageDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activity, setActivity] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (id) fetchActivity()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const fetchActivity = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('activity_logs')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      setActivity(data)
    } catch (error) {
      console.error('Error fetching activity:', error)
    } finally {
      setLoading(false)
    }
  }

  const getEntityRoute = (entityType: string, entityId: string) => {
    switch (entityType) {
      case 'lead': return `/leads/view/${entityId}`
      case 'customer': return `/customers/view/${entityId}`
      case 'property': return `/properties/view/${entityId}`
      case 'proposal': return `/proposals/view/${entityId}`
      case 'contract': return `/contracts/details/${entityId}`
      case 'job': return `/jobs/details/${entityId}`
      default: return '#'
    }
  }

  if (loading) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <div className="text-lg text-gray-600">Loading activity details...</div>
      </MobilePage>
    )
  }

  if (!activity) {
    return (
      <MobilePage className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <p className="text-gray-500 mb-4">Activity not found</p>
        <button
          type="button"
          onClick={() => navigate('/messages?tab=external')}
          className={mobile.primaryBtn}
        >
          Back to Inbox
        </button>
      </MobilePage>
    )
  }

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
      <MobilePageHeader
        sticky
        title="Activity Details"
        subtitle="Detailed view of activity log entry"
        onBack={() => navigate('/messages?tab=external')}
        backLabel="Back to Inbox"
      />

      <div className="space-y-6">
        <ViewSection
          title={
            <span className="capitalize">
              {activity.activity_type?.replace(/_/g, ' ') || 'Activity'}
            </span>
          }
          icon={<MessageSquareIcon className="w-6 h-6" />}
          tone="blue"
        >
          <p className="text-sm text-gray-900">{activity.description || 'No description'}</p>
          <p className="text-sm text-gray-500 mt-1">
            {new Date(activity.created_at).toLocaleString()}
          </p>

          {activity.metadata && Object.keys(activity.metadata).length > 0 && (
            <div className="bg-white rounded-lg border border-blue-100 p-4 shadow-sm mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Additional Details</h3>
              <div className="space-y-1">
                {Object.entries(activity.metadata).map(([key, value]) => (
                  <div key={key} className="flex gap-2 text-sm">
                    <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                    <span className="text-gray-900">{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ViewSection>

        <ViewSection title="Related Information" icon={<UserIcon className="w-6 h-6" />} tone="teal">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <UserIcon className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Entity Type:</span>
              <span className="ml-1 font-medium capitalize">{activity.entity_type || 'N/A'}</span>
            </div>
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Date:</span>
              <span className="ml-1 font-medium">
                {new Date(activity.created_at).toLocaleDateString()}
              </span>
            </div>
            {activity.entity_id && (
              <div className="flex items-center gap-2">
                <MapPinIcon className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Entity ID:</span>
                <span className="ml-1 font-medium text-xs">{activity.entity_id}</span>
              </div>
            )}
          </div>

          {activity.entity_type && activity.entity_id && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => navigate(getEntityRoute(activity.entity_type, activity.entity_id))}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                View {activity.entity_type?.charAt(0).toUpperCase() + activity.entity_type?.slice(1)}
              </button>
            </div>
          )}
        </ViewSection>
      </div>
    </MobilePage>
  )
}
